import PropTypes from 'prop-types';
import { createContext, useEffect, useReducer } from 'react';

// third-party
import { Chance } from 'chance';
import jwtDecode from 'jwt-decode';

// reducer - state management
import { LOGIN, LOGOUT } from '../store/actions';
import authReducer from '../store/accountReducer';

// project import
import Loader from '../components/Loader/Loader';
import axios from '../utils/authAxios';
import { loginByAuth } from '../views/auth/signin/AuthLogin';
// import { toast } from 'react-toastify';

const chance = new Chance();

// constant
const initialState = {
  isLoggedIn: false,
  isInitialized: false,
  user: null
};

const verifyToken = (accessToken) => {
  if (!accessToken || accessToken === 'undefined') {
    return false;
  }
  try {
    const decoded = jwtDecode(accessToken);
    return decoded.exp > Date.now() / 1000;
  } catch (error) {
    console.error('Token verification failed:', error);
    return false;
  }
};

const setSession = (accessToken) => {
  if (accessToken) {
    localStorage.setItem('accessToken', accessToken);
    axios.defaults.headers.common.Authorization = `Bearer ${accessToken}`;
  } else {
    localStorage.removeItem('accessToken');
    delete axios.defaults.headers.common.Authorization;
  }
};

// ==============================|| JWT CONTEXT & PROVIDER ||============================== //

const JWTContext = createContext(null);

export const JWTProvider = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  useEffect(() => {
    const init = async () => {
      try {
        const accessToken = window.localStorage.getItem('accessToken');
        if (accessToken && verifyToken(accessToken)) {
          setSession(accessToken);
          const userInfo = JSON.parse(window.localStorage.getItem('userInfo'));
          dispatch({
            type: LOGIN,
            payload: {
              isLoggedIn: true,
              user: userInfo
            }
          });
        } else {
          dispatch({
            type: LOGOUT
          });
        }
      } catch (err) {
        console.error(err);
        dispatch({
          type: LOGOUT
        });
      }
    };

    init();
  }, []);

  const login = async (values) => {
    console.log('login....', values);
    // const response = await axios.post('/api/account/login', { email, password });
    // const { serviceToken, user } = response.data;
    // setSession(serviceToken);

    const response = await loginByAuth(values);
    console.log('response....', response);

    if (response?.data && response.data.success === false) {
      if (!response.data.data?.accessToken) {
        return response;
      }
    }

    const apiData = response.data.data;
    const accessToken = apiData.accessToken;
    const refreshToken = apiData.refreshToken;
    const userInfo = apiData.userInfo;

    if (apiData.isEmailVerified !== undefined) userInfo.isEmailVerified = apiData.isEmailVerified;
    if (apiData.isPasswordSet !== undefined) userInfo.isPasswordSet = apiData.isPasswordSet;
    else if (apiData.password_set !== undefined) userInfo.isPasswordSet = apiData.password_set;
    if (apiData.permissions !== undefined) userInfo.permissions = apiData.permissions;
    const subInfo = response.data.subscription;
    if (subInfo && typeof subInfo === 'object' && subInfo.isSubscribe !== undefined) {
      if (subInfo.isSubscribe === false) {
        userInfo.subscription = false;
      } else {
        userInfo.subscription = subInfo.tier || true;
      }
    } else if (
      apiData.subscription === false ||
      apiData.subscription === 'false' ||
      apiData.has_subscription === false ||
      apiData.has_subscription === 'false'
    ) {
      userInfo.subscription = false;
    } else if (apiData.has_subscription === true) {
      userInfo.subscription = true;
    }

    // Derive template_id and is_template_assigned from apiData or userInfo
    const templateId = apiData.template_id || apiData.assigned_template_id || userInfo?.template_id || userInfo?.assigned_template_id;
    if (userInfo) {
      if (templateId) {
        userInfo.template_id = Number(templateId);
        userInfo.is_template_assigned = true;
      } else if (userInfo.is_template_assigned === undefined) {
        userInfo.is_template_assigned = !!userInfo.template_id;
      }
    }

    setSession(accessToken);
    localStorage.setItem('userInfo', JSON.stringify(userInfo));
    localStorage.setItem('accessToken', accessToken);
    localStorage.setItem('refreshToken', refreshToken);

    dispatch({
      type: LOGIN,
      payload: {
        isLoggedIn: true,
        user: userInfo
      }
    });
    return response;
  };

  const register = async (email, password, firstName, lastName) => {
    // todo: this flow need to be recode as it not verified
    const id = chance.bb_pin();
    const response = await axios.post('/api/account/register', {
      id,
      email,
      password,
      firstName,
      lastName
    });
    let users = response.data;

    if (window.localStorage.getItem('users') !== undefined && window.localStorage.getItem('users') !== null) {
      const localUsers = window.localStorage.getItem('users');
      users = [
        ...JSON.parse(localUsers),
        {
          id,
          email,
          password,
          name: `${firstName} ${lastName}`
        }
      ];
    }

    window.localStorage.setItem('users', JSON.stringify(users));
  };

  const logout = async () => {
    const accessToken = localStorage.getItem('accessToken');
    const refreshToken = localStorage.getItem('refreshToken');

    let response;
    try {
      response = await axios.post('auth/logout', { accessToken, refreshToken });
    } catch (err) {
      console.error('Logout API call failed', err);
    }

    setSession(null);
    localStorage.removeItem('userInfo');
    localStorage.removeItem('refreshToken');
    dispatch({ type: LOGOUT });

    // Hard redirect clears React Router state and replaces current history entry
    window.location.replace('/login');

    return response;
  };

  const resetPassword = async () => {};

  const updateProfile = () => {
    try {
      const userInfo = JSON.parse(window.localStorage.getItem('userInfo'));
      dispatch({
        type: LOGIN,
        payload: {
          isLoggedIn: true,
          user: userInfo
        }
      });
    } catch (e) {
      console.error('Failed to update profile in context', e);
    }
  };

  if (state.isInitialized !== undefined && !state.isInitialized) {
    return <Loader />;
  }

  return <JWTContext.Provider value={{ ...state, login, logout, register, resetPassword, updateProfile }}>{children}</JWTContext.Provider>;
};

JWTProvider.propTypes = {
  children: PropTypes.node
};

export default JWTContext;
